/**
=========================================================
* Fruits Admin Panel React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState, forwardRef } from "react";

// react-router-dom components
import { useLocation, NavLink, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LogoutIcon from "@mui/icons-material/Logout";
import CancelIcon from "@mui/icons-material/Cancel";
import MenuIcon from "@mui/icons-material/Menu";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import Slide from "@mui/material/Slide";
import Grow from "@mui/material/Grow";
import Zoom from "@mui/material/Zoom";
import Fade from "@mui/material/Fade";
import CircularProgress from "@mui/material/CircularProgress";
import { keyframes } from "@mui/system";
import Backdrop from "@mui/material/Backdrop";

// Fruits Admin Panel React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Fruits Admin Panel React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Fruits Admin Panel React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

// Premium Animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(244, 67, 54, 0.2), 0 0 10px rgba(244, 67, 54, 0.1); }
  50% { box-shadow: 0 0 20px rgba(244, 67, 54, 0.6), 0 0 30px rgba(244, 67, 54, 0.3); }
  100% { box-shadow: 0 0 5px rgba(244, 67, 54, 0.2), 0 0 10px rgba(244, 67, 54, 0.1); }
`;

const rotate360 = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const bounceIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  70% { transform: scale(0.9); }
  100% { transform: scale(1); opacity: 1; }
`;

const slideInLeft = keyframes`
  from { transform: translateX(-100px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const slideInRight = keyframes`
  from { transform: translateX(100px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const borderFlow = keyframes`
  0% { border-image-source: linear-gradient(0deg, #f44336, #ff7961); }
  25% { border-image-source: linear-gradient(90deg, #f44336, #ff7961); }
  50% { border-image-source: linear-gradient(180deg, #f44336, #ff7961); }
  75% { border-image-source: linear-gradient(270deg, #f44336, #ff7961); }
  100% { border-image-source: linear-gradient(360deg, #f44336, #ff7961); }
`;

// Transition component
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} timeout={500} />;
});

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const firstSegment = location.pathname.split("/")[1] || "";
  const segmentToMenuKey = {
    "users-details": "users",
    "post-details": "posts",
    "usercontact-details": "usercontact",
    "news-details": "news",
    "admin-post-details": "admin-post",
  };
  const collapseName = segmentToMenuKey[firstSegment] || firstSegment;
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [animateIcon, setAnimateIcon] = useState(false);

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const openSidenav = () => setMiniSidenav(dispatch, false);
  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const confirmLogout = async () => {
    setIsLoggingOut(true);
    setTimeout(() => {
      localStorage.clear();
      sessionStorage.clear();
      navigate("/authentication/sign-in");
      setLogoutDialogOpen(false);
      setIsLoggingOut(false);
    }, 1000);
  };

  const cancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route }) => {
    let returnValue;

    if (type === "collapse") {
      returnValue = href ? (
        <Link
          href={href}
          key={key}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavCollapse
            name={name}
            icon={icon}
            active={key === collapseName}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink key={key} to={route}>
          <SidenavCollapse name={name} icon={icon} active={key === collapseName} />
        </NavLink>
      );
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  return (
    <>
      <SidenavRoot
        {...rest}
        variant={isMobile ? "temporary" : "permanent"}
        open={!miniSidenav}
        onClose={closeSidenav}
        ModalProps={{ keepMounted: true }}
        ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
      >
        <MDBox
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            overflow: "hidden",
          }}
        >
          <MDBox pt={3} pb={1} px={4} textAlign="center">
            <MDBox
              display={{ xs: "block", xl: "none" }}
              position="absolute"
              top={0}
              right={0}
              p={1.625}
              onClick={closeSidenav}
              sx={{ cursor: "pointer" }}
            >
              <MDTypography variant="h6" color="secondary">
                <Icon sx={{ fontWeight: "bold" }}>close</Icon>
              </MDTypography>
            </MDBox>
            <MDBox component={NavLink} to="/" display="flex" alignItems="center">
              {brand && <MDBox component="img" src={brand} alt="Brand" width="3rem" />}
              <MDBox
                width={!brandName && "100%"}
                sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
              >
                <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
                  {brandName}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
          <Divider
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
          <MDBox
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(255,255,255,0.3)",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(255,255,255,0.5)",
              },
            }}
          >
            <List>{renderRoutes}</List>
          </MDBox>
          <MDBox p={2} sx={{ flexShrink: 0 }}>
            <MDButton
              onClick={handleLogout}
              variant="gradient"
              color={sidenavColor}
              fullWidth
              sx={{
                position: "relative",
                overflow: "hidden",
                transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 8px 20px rgba(0, 0, 0, 0.2)`,
                },
              }}
            >
              <Icon sx={{ mr: 1 }}>logout</Icon>
              Logout
            </MDButton>
          </MDBox>
        </MDBox>
      </SidenavRoot>

      {/* Premium Logout Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={!isLoggingOut ? cancelLogout : null}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
          },
        }}
        sx={{
          "& .MuiDialog-paper": {
            background: darkMode
              ? "linear-gradient(135deg, #1e1e2f 0%, #2d2d3f 100%)"
              : "linear-gradient(135deg, #ffffff 0%, #fef9f9 100%)",
            borderRadius: "32px",
            padding: "32px",
            position: "relative",
            overflow: "visible",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.3)",
            border: "1px solid rgba(244, 67, 54, 0.2)",
            animation: `${float} 3s ease-in-out infinite`,
          },
        }}
      >
        {/* Animated Border Gradient */}
        <MDBox
          sx={{
            position: "absolute",
            top: -2,
            left: -2,
            right: -2,
            bottom: -2,
            background: "linear-gradient(45deg, #f44336, #ff7961, #f44336, #ff7961)",
            borderRadius: "34px",
            zIndex: -1,
            opacity: 0.3,
            animation: `${glow} 2s ease-in-out infinite`,
          }}
        />

        {/* Floating Particles */}
        <MDBox
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
            borderRadius: "32px",
            pointerEvents: "none",
          }}
        >
          {[...Array(20)].map((_, i) => (
            <MDBox
              key={i}
              sx={{
                position: "absolute",
                width: `${Math.random() * 10 + 2}px`,
                height: `${Math.random() * 10 + 2}px`,
                background: `rgba(244, 67, 54, ${Math.random() * 0.3 + 0.1})`,
                borderRadius: "50%",
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animation: `${float} ${Math.random() * 5 + 3}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`,
              }}
            />
          ))}
        </MDBox>

        {/* Main Content */}
        <MDBox sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
          {/* Animated Icon Section */}
          <Grow in={logoutDialogOpen} timeout={600}>
            <MDBox
              sx={{
                mb: 3,
                display: "inline-block",
                animation: `${bounceIn} 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)`,
              }}
            >
              <MDBox
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MDBox
                  sx={{
                    position: "absolute",
                    width: "120px",
                    height: "120px",
                    borderRadius: "50%",
                    background:
                      "radial-gradient(circle, rgba(244,67,54,0.2) 0%, rgba(244,67,54,0) 70%)",
                    animation: `${pulse} 2s ease-in-out infinite`,
                  }}
                />
                <MDBox
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #f44336 0%, #ff7961 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 10px 30px rgba(244, 67, 54, 0.4)",
                    animation: `${glow} 2s ease-in-out infinite`,
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.1) rotate(5deg)",
                    },
                  }}
                >
                  <LogoutIcon sx={{ fontSize: 50, color: "#fff" }} />
                </MDBox>
              </MDBox>
            </MDBox>
          </Grow>

          {/* Title */}
          <Zoom in={logoutDialogOpen} timeout={500} style={{ transitionDelay: "100ms" }}>
            <MDTypography
              variant="h3"
              fontWeight="bold"
              sx={{
                background: "linear-gradient(135deg, #f44336 0%, #ff7961 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                mb: 2,
                letterSpacing: "-0.5px",
              }}
            >
              Logout Confirmation
            </MDTypography>
          </Zoom>

          {/* Message */}
          <Fade in={logoutDialogOpen} timeout={800} style={{ transitionDelay: "200ms" }}>
            <MDBox sx={{ mb: 4 }}>
              <MDTypography
                variant="h6"
                color={darkMode ? "white" : "text"}
                sx={{
                  fontWeight: "regular",
                  lineHeight: 1.6,
                  background: darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(244, 67, 54, 0.05)",
                  padding: "20px",
                  borderRadius: "20px",
                  border: `1px solid ${darkMode ? "rgba(255,255,255,0.1)" : "rgba(244,67,54,0.2)"}`,
                }}
              >
                Are you sure you want to logout?
                <br />
                <MDTypography component="span" variant="body2" color="error" fontWeight="bold">
                  You will need to login again to access your account.
                </MDTypography>
              </MDTypography>
            </MDBox>
          </Fade>

          {/* Action Buttons */}
          <MDBox
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Zoom in={logoutDialogOpen} timeout={600} style={{ transitionDelay: "300ms" }}>
              <MDButton
                onClick={cancelLogout}
                disabled={isLoggingOut}
                variant="outlined"
                fullWidth={false}
                sx={{
                  minWidth: 150,
                  py: 1.5,
                  borderRadius: "16px",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  textTransform: "none",
                  borderWidth: "2px",
                  color: darkMode ? "white" : "text.primary",
                  borderColor: darkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)",
                  transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    borderWidth: "2px",
                    borderColor: "#f44336",
                    backgroundColor: "rgba(244, 67, 54, 0.05)",
                  },
                }}
                startIcon={<CancelIcon />}
              >
                Stay Here
              </MDButton>
            </Zoom>

            <Zoom in={logoutDialogOpen} timeout={600} style={{ transitionDelay: "400ms" }}>
              <MDButton
                onClick={confirmLogout}
                disabled={isLoggingOut}
                variant="gradient"
                color="error"
                fullWidth={false}
                sx={{
                  minWidth: 150,
                  py: 1.5,
                  borderRadius: "16px",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  textTransform: "none",
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                  boxShadow: "0 8px 20px rgba(244, 67, 54, 0.4)",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 12px 30px rgba(244, 67, 54, 0.6)",
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                    transition: "left 0.5s ease",
                  },
                  "&:hover::before": {
                    left: "100%",
                  },
                }}
                startIcon={
                  isLoggingOut ? <CircularProgress size={20} color="inherit" /> : <LogoutIcon />
                }
              >
                {isLoggingOut ? "Logging out..." : "Yes, Logout"}
              </MDButton>
            </Zoom>
          </MDBox>

          {/* Warning Text */}
          <Fade in={logoutDialogOpen} timeout={1000} style={{ transitionDelay: "500ms" }}>
            <MDBox sx={{ mt: 3 }}>
              <MDTypography
                variant="caption"
                color="error"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  opacity: 0.8,
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 16 }} />
                This action cannot be undone
              </MDTypography>
            </MDBox>
          </Fade>
        </MDBox>
      </Dialog>
    </>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
